import React, { useCallback, useEffect, useImperativeHandle, useState } from 'react';
import Hotel from '@app/model/hotel/Hotel';
import { useForm } from 'react-hook-form';
import has from 'lodash/has';
import get from 'lodash/get';
import { useTranslation } from '@app/component/translation/Translation';
import useModal from '@app/component/overlays/modal/ModalRef';
import Modal from '@app/component/overlays/modal/Modal';
import Message from '@app/component/message/Message';
import FormButton from '@app/component/form/button/FormButton';
import { useMutation } from '@tanstack/react-query';
import HotelService from '@app/model/hotel/service/HotelService';
import ProcessRequestType from '@app/model/process_request/ProcessRequestType';
import ProcessRequestState from '@app/model/process_request/ProcessRequestState';
import ProcessRequestResult from '@app/model/process_request/ProcessRequestResult';
import ProfileProcessRequestOutput from '@app/model/process_request/ProcessRequestOutput';
import WebSocketMessageProvider from '@app/component/web-socket/WebSocketMessageProvider';
import FormInputText from '@app/component/form/input/text/FormInputText';

const AddNewHotelFromBookingUrlModal: React.FunctionComponent<{
	onCreate: (hotel: Hotel) => any,
	ref: any
}> = React.forwardRef(({ onCreate }, ref: React.Ref<any>) => {

	const modal = useModal();
	const form = useForm();
	const [errorMessage, setErrorMessage] = useState<any>();
	const [isProcessing, setIsProcessing] = useState(false);
	const [hotelId, setHotelId] = useState<number>();
	const { t } = useTranslation();

	useEffect(() => {
		const hasError = !!form && has(form.formState.errors, 'url');
		const errorMessage = hasError ? (get(form.formState.errors, 'url.message') || t('common.form.error.required')) : '';
		setErrorMessage(errorMessage);
	}, [form.formState.errors]);

	const submit = useCallback((data: { url: string }) => {
		const regex = /https:\/\/www\.?booking\.com\/hotel/gm;
		if (!data.url.match(regex)) {
			form.setError('url', { message: 'Please use correct booking url' });
		} else {
			addHotelFromBookingUrl.mutate(data.url);
		}
	}, [onCreate]);

	const addHotelFromBookingUrl = useMutation({
		mutationFn: HotelService.addHotelFromBookingUrl,
		onSuccess: (data) => {
			setIsProcessing(true);
			setHotelId(data);
		},
	});

	const findHotelById = useMutation({
		mutationFn: HotelService.findHotelById,
		onSuccess: (data) => {
			setIsProcessing(false);
			modal.close();
			onCreate(data);
		},
	});

	const handleMessage = (output: ProfileProcessRequestOutput) => {
		if (!!output && output.state === ProcessRequestState.FINISHED) {
			if (output.result === ProcessRequestResult.SUCCESS || output.result === ProcessRequestResult.SUCCESS_NO_CHILDREN) {
				findHotelById.mutate(hotelId!);
			} else if (output.result === ProcessRequestResult.FAILED) {
				setErrorMessage('Failed to import hotel');
				setIsProcessing(false);
			}
		}
	};

	useImperativeHandle(ref, () => {
		return {
			open: () => modal.open(),
		};
	});

	return <WebSocketMessageProvider subscriptions={[{
		messageType: ProcessRequestType.MANUAL_HOTEL_INFO,
		handler: handleMessage,
	}]}>
		<Modal title="Insert Hotel's Booking Link" ref={modal.ref} actions={
			<FormButton state="primary" isLoading={addHotelFromBookingUrl.isPending || isProcessing}
									onClick={form.handleSubmit(submit)}>
				Submit
			</FormButton>
		}>
			<Message state="info">Provide the hotel's booking link below. Once added, you can set it as
				your property or as a competitor.</Message>
			<div className="mt-8">
				<div className="flex justify-between"><label htmlFor="input-normal" className="label">Set booking link</label>
				</div>
				<div className="mt-0.5">
					<FormInputText name="url" form={form} disabled={addHotelFromBookingUrl.isPending}
												 placeholder="https://www.booking.com/...." />
				</div>
			</div>
		</Modal>
	</WebSocketMessageProvider>;
});

export default AddNewHotelFromBookingUrlModal;